import SingleDegreeImg from '../../Images/SingleDegreeImg.jpg'
import FreeVibrationEquationsImg from '../../Images/FreeVibrationEquationsImg.jpg'

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Shared helpers ────────────────────────────────────────────────
function springPath(x0, x1, y, n=9) {
  const cW = (x1-x0)/n;
  const pts = [[x0,y]];
  for (let i=0;i<n;i++) {
    pts.push([x0+i*cW+cW*0.25, y-17]);
    pts.push([x0+i*cW+cW*0.75, y+17]);
  }
  pts.push([x1,y]);
  return pts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
}

function Wall({ x, y, h, dir='left' }) {
  const ticks = [];
  for (let ty=y; ty<y+h; ty+=18) ticks.push(ty);
  return (
    <>
      <rect x={x} y={y} width={12} height={h} fill="#888"/>
      {ticks.map(ty => (
        <line key={ty}
          x1={dir==='left'?x:x+12} y1={ty}
          x2={dir==='left'?x-14:x+26} y2={ty+14}
          stroke="#888" strokeWidth="1.5"/>
      ))}
    </>
  );
}

function Ground({ x1, x2, y }) {
  const ticks = [];
  for (let tx=x1; tx<=x2; tx+=22) ticks.push(tx);
  return (
    <>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#555" strokeWidth="2"/>
      {ticks.map(tx => (
        <line key={tx} x1={tx} y1={y} x2={tx-12} y2={y+16} stroke="#555" strokeWidth="1.5"/>
      ))}
    </>
  );
}

function Damper({ x0, x1, y }) {
  const mid = (x0+x1)/2;
  return (
    <>
      {/* outer cylinder */}
      <rect x={x0} y={y-13} width={(x1-x0)*0.55} height={26} rx={4} fill="#ddd" stroke="#999" strokeWidth="1.5"/>
      <rect x={x0+2} y={y-11} width={(x1-x0)*0.55-4} height={22} rx={3} fill="#b3cde3" opacity="0.7"/>
      {/* piston plate */}
      <rect x={x0+(x1-x0)*0.55-5} y={y-14} width={10} height={28} rx={2} fill="#555"/>
      {/* rod */}
      <rect x={x0+(x1-x0)*0.55+5} y={y-6} width={(x1-x0)*0.45-5} height={12} rx={2} fill="#888"/>
    </>
  );
}

// ── Diagram 1: SDOF System Setup & Free Body Diagram ─────────────
function DiagramSDOFSetup() {
  const W=900, H=400;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>SDOF System — Physical Model &amp; Free Body Diagram</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="aR" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
          <marker id="aB" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#2E75B6"/></marker>
          <marker id="aG" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#27ae60"/></marker>
          <marker id="aO" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#e07b00"/></marker>
        </defs>

        {/* ── LEFT: Physical model ──────────────────────────── */}
        <rect x={10} y={10} width={410} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={215} y={33} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">Physical Model</text>

        {/* Wall */}
        <Wall x={18} y={60} h={270}/>

        {/* Spring (top path) */}
        <path d={springPath(30,195,120,9)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
        <text x={112} y={103} textAnchor="middle" fontSize="14" fill="#27ae60" fontWeight="700">k</text>

        {/* Damper (bottom path) */}
        <Damper x0={30} x1={195} y={195}/>
        <text x={112} y={182} textAnchor="middle" fontSize="14" fill="#888" fontWeight="700">c</text>

        {/* Connect lines from wall to spring/damper start */}
        <line x1={30} y1={120} x2={30} y2={195} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4"/>
        {/* Connect lines from spring/damper end to mass */}
        <line x1={195} y1={120} x2={195} y2={195} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4"/>

        {/* Mass block */}
        <rect x={195} y={130} width={120} height={100} rx={6} fill="#2E75B6" stroke="#1a5276" strokeWidth="2.5"/>
        <text x={255} y={186} textAnchor="middle" fontSize="26" fill="#fff" fontWeight="700">m</text>

        {/* Ground */}
        <Ground x1={30} x2={400} y={340}/>

        {/* Rollers under mass */}
        {[220,270,305].map(cx=>(
          <g key={cx}>
            <circle cx={cx} cy={340} r={13} fill="#888" stroke="#555" strokeWidth="2"/>
            <circle cx={cx} cy={340} r={4}  fill="#fff"/>
          </g>
        ))}

        {/* Applied force F(t) */}
        <line x1={315} y1={180} x2={385} y2={180} stroke="#c0392b" strokeWidth="3.5" markerEnd="url(#aR)"/>
        <text x={390} y={185} fontSize="15" fill="#c0392b" fontWeight="700">F(t)</text>

        {/* Displacement x */}
        <line x1={210} y1={122} x2={320} y2={122} stroke="#2E75B6" strokeWidth="2" strokeDasharray="5" markerEnd="url(#aB)"/>
        <text x={263} y={114} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">x(t)</text>
        <text x={207} y={114} fontSize="11" fill="#2E75B6">0</text>

        {/* Equilibrium dashed */}
        <line x1={195} y1={340} x2={315} y2={340} stroke="#aaa" strokeWidth="1" strokeDasharray="3"/>
        <text x={356} y={336} fontSize="11" fill="#aaa">x=0</text>

        {/* ── RIGHT: Free body diagram ──────────────────────────── */}
        <rect x={430} y={10} width={460} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={660} y={33} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">Free Body Diagram</text>

        {/* Isolated mass */}
        <rect x={560} y={140} width={200} height={120} rx={6} fill="#2E75B6" stroke="#1a5276" strokeWidth="2.5"/>
        <text x={660} y={207} textAnchor="middle" fontSize="26" fill="#fff" fontWeight="700">m</text>
        {/* Shadow/3-D effect */}
        <rect x={563} y={263} width={200} height={6} rx={3} fill="#1a5276" opacity="0.3"/>

        {/* Force arrows on FBD */}
        {/* F(t) → right */}
        <line x1={760} y1={200} x2={840} y2={200} stroke="#c0392b" strokeWidth="3.5" markerEnd="url(#aR)"/>
        <text x={845} y={205} fontSize="13" fill="#c0392b" fontWeight="700">F(t)</text>

        {/* Spring force ← left */}
        <line x1={560} y1={175} x2={465} y2={175} stroke="#27ae60" strokeWidth="3.5" markerEnd="url(#aG)"/>
        <text x={435} y={170} fontSize="13" fill="#27ae60" fontWeight="700">kx</text>

        {/* Damper force ← left */}
        <line x1={560} y1={225} x2={465} y2={225} stroke="#888" strokeWidth="3.5" markerEnd="url(#aB)"/>
        <text x={430} y={231} fontSize="13" fill="#555" fontWeight="700">cẋ</text>

        {/* Inertia (mẍ) ← */}
        <line x1={660} y1={140} x2={660} y2={65} stroke="#e07b00" strokeWidth="3.5" markerEnd="url(#aO)"/>
        <text x={668} y={58} fontSize="13" fill="#e07b00" fontWeight="700">mẍ</text>

        {/* Newton equation box */}
        <rect x={436} y={310} width={448} height={72} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={660} y={332} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Newton's 2nd Law — Sum of forces = mẍ</text>
        <text x={660} y={352} textAnchor="middle" fontSize="13" fill="#1a1a2e" fontFamily="'Courier New',monospace">F(t) − kx − cẋ = mẍ</text>
        <text x={660} y={372} textAnchor="middle" fontSize="13" fill="#1a1a2e" fontFamily="'Courier New',monospace">∴  mẍ + cẋ + kx = F(t)</text>
      </svg>
      <div style={S.dCaption}>
        Left: the physical SDOF model — mass m on frictionless rollers, connected to a fixed wall via a spring (stiffness k) in parallel with a viscous damper (coefficient c), driven by external force F(t). Right: isolating the mass and applying Newton's 2nd Law to each force yields the governing equation of motion.
      </div>
    </div>
  );
}

// ── Diagram 2: Free Undamped Vibration ───────────────────────────
function DiagramFreeUndamped() {
  const W=900, H=340;

  // Generate oscillation: x(t) = A cos(ωn t), ωn=2π rad/s, A=1
  const ωn=2*Math.PI, A=1, dt=0.02, tMax=3;
  const pts=[];
  for(let t=0;t<=tMax;t+=dt) pts.push([t, A*Math.cos(ωn*t)]);

  // Map to SVG  x:[0,3]→[60,560]  y:[-1.3,1.3]→[285,55]
  const mx = t => 60+(t/tMax)*500;
  const my = y => 170 - y*100;
  const path = pts.map(([t,y],i)=>`${i===0?'M':'L'}${mx(t).toFixed(1)},${my(y).toFixed(1)}`).join(' ');

  // Period markers
  const T=1; // 1 second

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Free Undamped Vibration — Simple Harmonic Motion</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
          <marker id="axT" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
        </defs>

        {/* ── LEFT: waveform ─────────────────────────── */}
        <rect x={10} y={10} width={560} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={290} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">x(t) = A cos(ωₙt)  —  SHM waveform</text>

        {/* Grid */}
        {[-1,-0.5,0,0.5,1].map(v=>(
          <g key={v}>
            <line x1={60} y1={my(v)} x2={565} y2={my(v)} stroke="#f0e0e0" strokeWidth={v===0?1.5:0.8} strokeDasharray={v===0?'':'4,3'}/>
            <text x={54} y={my(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,1,2,3].map(t=>(
          <g key={t}>
            <line x1={mx(t)} y1={50} x2={mx(t)} y2={295} stroke="#f0e0e0" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={mx(t)} y={308} textAnchor="middle" fontSize="10" fill="#888">{t}s</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={58} y1={50} x2={58} y2={300} stroke="#555" strokeWidth="1.5" markerEnd="url(#axA)"/>
        <line x1={55} y1={295} x2={568} y2={295} stroke="#555" strokeWidth="1.5" markerEnd="url(#axA)"/>
        <text x={22} y={170} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,22,170)">x(t)</text>
        <text x={572} y={300} fontSize="12" fill="#555">t</text>

        {/* Curve */}
        <path d={path} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Amplitude annotation */}
        <line x1={60} y1={my(1)} x2={60} y2={my(0)} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4"/>
        <text x={40} y={my(0.5)+4} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">A</text>
        <line x1={55} y1={my(1)} x2={65} y2={my(1)} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={55} y1={my(0)} x2={65} y2={my(0)} stroke="#2E75B6" strokeWidth="1.5"/>

        {/* Period annotation */}
        <line x1={mx(0)} y1={248} x2={mx(T)} y2={248} stroke="#c0392b" strokeWidth="2" markerEnd="url(#axT)"/>
        <line x1={mx(T)} y1={248} x2={mx(0)} y2={248} stroke="#c0392b" strokeWidth="2" markerEnd="url(#axT)"/>
        <text x={mx(T/2)} y={242} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">T = 2π/ωₙ</text>

        {/* Peak labels */}
        <text x={mx(0)+4} y={my(1)-8} fontSize="11" fill="#c0392b">A</text>
        <text x={mx(1)+4} y={my(1)-8} fontSize="11" fill="#c0392b">A</text>
        <text x={mx(0.5)+4} y={my(-1)+18} fontSize="11" fill="#c0392b">−A</text>

        {/* ── RIGHT: key parameters panel ────────────── */}
        <rect x={580} y={10} width={310} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={735} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Key Parameters</text>

        {[
          {y:44, bg:"#eaf3ff", bc:"#2E75B6", label:"Natural Frequency ωₙ", eq:"ωₙ = √(k/m)   [rad/s]", sub:"Governs oscillation speed"},
          {y:108, bg:"#f0fff4", bc:"#27ae60", label:"Natural Frequency fₙ", eq:"fₙ = ωₙ/(2π)   [Hz]", sub:"Cycles per second"},
          {y:172, bg:"#FEF0F0", bc:"#c0392b", label:"Period T", eq:"T = 1/fₙ = 2π/ωₙ   [s]", sub:"Time for one complete cycle"},
          {y:236, bg:"#fff5e6", bc:"#e07b00", label:"Amplitude A", eq:"A = √(x₀² + (ẋ₀/ωₙ)²)", sub:"Set by initial conditions"},
          {y:300, bg:"#f5d0ff", bc:"#9b59b6", label:"Phase angle φ", eq:"φ = arctan(ẋ₀/(ωₙx₀))", sub:"Time offset from reference"},
        ].map(({y,bg,bc,label,eq,sub})=>(
          <g key={y}>
            <rect x={586} y={y} width={298} height={58} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={592} y={y+17} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            <text x={592} y={y+35} fontSize="12" fill="#1a1a2e" fontFamily="'Courier New',monospace">{eq}</text>
            <text x={592} y={y+52} fontSize="10" fill="#666" fontStyle="italic">{sub}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Free undamped vibration produces simple harmonic motion. With no damping or forcing, the system oscillates forever at its natural frequency ωₙ. The amplitude and phase are fixed entirely by the initial displacement x₀ and velocity ẋ₀. The natural frequency depends only on stiffness k and mass m, not on amplitude.
      </div>
    </div>
  );
}

// ── Diagram 3: Damping Ratio Comparison ─────────────────────────
function DiagramDampingComparison() {
  const W=900, H=380;

  // Step response x(t) for different ζ, ωn=3 rad/s
  const ωn=3, dt=0.03, tMax=6;
  const configs = [
    { ζ:0.05, color:'#9b59b6', label:'ζ = 0.05 (lightly damped)' },
    { ζ:0.2,  color:'#c0392b', label:'ζ = 0.2' },
    { ζ:0.5,  color:'#e07b00', label:'ζ = 0.5' },
    { ζ:1.0,  color:'#27ae60', label:'ζ = 1.0 (critical)' },
    { ζ:2.0,  color:'#2E75B6', label:'ζ = 2.0 (overdamped)' },
  ];

  const stepResp = (ζ, t) => {
    if (ζ < 1) {
      const ωd = ωn*Math.sqrt(1-ζ*ζ);
      return 1 - Math.exp(-ζ*ωn*t)*(Math.cos(ωd*t) + (ζ/Math.sqrt(1-ζ*ζ))*Math.sin(ωd*t));
    } else if (ζ === 1) {
      return 1 - Math.exp(-ωn*t)*(1+ωn*t);
    } else {
      const s1=-ωn*(ζ-Math.sqrt(ζ*ζ-1)), s2=-ωn*(ζ+Math.sqrt(ζ*ζ-1));
      const A=s2/(s2-s1), B=-s1/(s2-s1);
      return 1 - A*Math.exp(s1*t) - B*Math.exp(s2*t);
    }
  };

  const mx = t => 55+(t/tMax)*510;
  const my = y => 300-(y/1.6)*240;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Step Response for Different Damping Ratios ζ</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axS" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
        </defs>

        {/* Graph area */}
        <rect x={50} y={40} width={520} height={265} rx={3} fill="#fafafa" stroke="#ddd"/>

        {/* Grid */}
        {[0,0.5,1.0,1.5].map(v=>(
          <g key={v}>
            <line x1={50} y1={my(v)} x2={572} y2={my(v)} stroke={v===1?"#ccc":"#ebebeb"} strokeWidth={v===1?1.5:1} strokeDasharray={v===1?"6,3":""}/>
            <text x={44} y={my(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,1,2,3,4,5,6].map(t=>(
          <g key={t}>
            <line x1={mx(t)} y1={40} x2={mx(t)} y2={305} stroke="#ebebeb" strokeWidth="1"/>
            <text x={mx(t)} y={320} textAnchor="middle" fontSize="10" fill="#888">{t}s</text>
          </g>
        ))}

        {/* Steady-state line */}
        <line x1={50} y1={my(1)} x2={572} y2={my(1)} stroke="#999" strokeWidth="1.5" strokeDasharray="8,3"/>
        <text x={576} y={my(1)+4} fontSize="11" fill="#999">1.0</text>

        {/* Curves */}
        {configs.map(({ζ,color})=>{
          const pts=[];
          for(let t=0;t<=tMax;t+=dt) pts.push([t,stepResp(ζ,t)]);
          const d=pts.map(([t,y],i)=>`${i===0?'M':'L'}${mx(t).toFixed(1)},${my(Math.min(y,1.58)).toFixed(1)}`).join(' ');
          return <path key={ζ} d={d} fill="none" stroke={color} strokeWidth="2.2"/>;
        })}

        {/* Axes */}
        <line x1={48} y1={40} x2={48} y2={308} stroke="#555" strokeWidth="1.5" markerEnd="url(#axS)"/>
        <line x1={46} y1={305} x2={576} y2={305} stroke="#555" strokeWidth="1.5" markerEnd="url(#axS)"/>
        <text x={22} y={170} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,22,170)">x(t) / x_ss</text>
        <text x={580} y={310} fontSize="12" fill="#555">t (s)</text>

        {/* ── RIGHT: Legend & regime panels ─── */}
        <rect x={580} y={10} width={310} height={H-15} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={735} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Damping Regimes</text>

        {configs.map(({color,label,ζ},i)=>{
          const descs = [
            ["Near-zero damping","Oscillates for very long time","Resonance peak is very tall"],
            ["Lightly damped","Several oscillations before settle","Common in metal structures"],
            ["Moderate damping","1–2 overshoots, fast settling","Typical control system target"],
            ["Critical  (ζ=1)","Fastest return, zero overshoot","c = 2√(km) exactly"],
            ["Overdamped (ζ>1)","No oscillation; sluggish","Slow to reach steady state"],
          ];
          const y = 42+i*64;
          const [title,...lines]=descs[i];
          return (
            <g key={i}>
              <rect x={586} y={y} width={298} height={58} rx={3} fill="#fafafa" stroke={color} strokeWidth="1.5"/>
              <line x1={592} y1={y+10} x2={616} y2={y+10} stroke={color} strokeWidth="3"/>
              <text x={622} y={y+14} fontSize="12" fill={color} fontWeight="700">{label}</text>
              {lines.map((l,j)=><text key={j} x={592} y={y+30+j*14} fontSize="10" fill="#555">{l}</text>)}
            </g>
          );
        })}

        {/* Overshoot annotation */}
        <line x1={mx(0.38)} y1={my(1)} x2={mx(0.38)} y2={my(1.28)} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="3"/>
        <text x={mx(0.38)+4} y={my(1.14)} fontSize="10" fill="#9b59b6" fontWeight="700">OS%</text>
      </svg>
      <div style={S.dCaption}>
        The step response character depends entirely on ζ. As ζ increases from near 0 to above 1, the response transitions from highly oscillatory (underdamped) through the optimal critically-damped case to a sluggish overdamped response. Real engineering systems typically target ζ ≈ 0.5–0.7 to balance speed and overshoot.
      </div>
    </div>
  );
}

// ── Diagram 4: Free Damped Vibration — Logarithmic Decrement ────
function DiagramLogDecrement() {
  const W=900, H=340;

  const ωn=2*Math.PI, ζ=0.12, ωd=ωn*Math.sqrt(1-ζ*ζ);
  const dt=0.01, tMax=4;
  const pts=[];
  for(let t=0;t<=tMax;t+=dt) {
    const x = Math.exp(-ζ*ωn*t)*Math.cos(ωd*t);
    pts.push([t,x]);
  }

  const mx = t => 55+(t/tMax)*490;
  const my = y => 170-y*110;

  // Peak times for ζ=0.12, ωd≈2π
  const Td=2*Math.PI/ωd;
  const peaks = [0, Td, 2*Td, 3*Td];
  const peakAmps = peaks.map(t=>Math.exp(-ζ*ωn*t));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Free Damped Vibration — Exponential Envelope &amp; Logarithmic Decrement</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axD" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
          <marker id="axDR" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
        </defs>

        {/* ── LEFT: waveform ──────────────────── */}
        <rect x={10} y={10} width={565} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={290} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">x(t) = Ae^(−ζωₙt)cos(ωd t)  [ζ=0.12]</text>

        {/* Grid */}
        {[-1,-0.5,0,0.5,1].map(v=>(
          <g key={v}>
            <line x1={55} y1={my(v)} x2={548} y2={my(v)} stroke={v===0?"#ccc":"#f0e8e8"} strokeWidth={v===0?1.5:0.8} strokeDasharray={v===0?'':'4,3'}/>
            <text x={49} y={my(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,1,2,3,4].map(t=>(
          <g key={t}>
            <line x1={mx(t)} y1={40} x2={mx(t)} y2={295} stroke="#f0e8e8" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={mx(t)} y={310} textAnchor="middle" fontSize="10" fill="#888">{t}s</text>
          </g>
        ))}

        {/* Exponential envelope (positive & negative) */}
        {[1,-1].map(sign=>{
          const envPts=[];
          for(let t=0;t<=tMax;t+=0.02) envPts.push([t,sign*Math.exp(-ζ*ωn*t)]);
          const d=envPts.map(([t,y],i)=>`${i===0?'M':'L'}${mx(t).toFixed(1)},${my(y).toFixed(1)}`).join(' ');
          return <path key={sign} d={d} fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7"/>;
        })}
        <text x={mx(1.5)+5} y={my(Math.exp(-ζ*ωn*1.5))-8} fontSize="11" fill="#e07b00" fontStyle="italic">e^(−ζωₙt)</text>

        {/* Main damped oscillation curve */}
        <path d={pts.map(([t,y],i)=>`${i===0?'M':'L'}${mx(t).toFixed(1)},${my(y).toFixed(1)}`).join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Peak markers & logarithmic decrement annotation */}
        {peaks.slice(0,3).map((t,i)=>{
          const xp=mx(t), yp=my(peakAmps[i]);
          return (
            <g key={i}>
              <circle cx={xp} cy={yp} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
              <text x={xp+7} y={yp-5} fontSize="11" fill="#2E75B6" fontWeight="700">x{i+1}</text>
            </g>
          );
        })}

        {/* δ annotation between x1 and x2 */}
        <line x1={mx(0)} y1={my(peakAmps[0])} x2={mx(Td)} y2={my(peakAmps[0])} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4"/>
        <line x1={mx(Td)} y1={my(peakAmps[0])} x2={mx(Td)} y2={my(peakAmps[1])} stroke="#2E75B6" strokeWidth="2.5"/>
        <text x={mx(Td)+8} y={(my(peakAmps[0])+my(peakAmps[1]))/2} fontSize="12" fill="#2E75B6" fontWeight="700">δ = ln(x₁/x₂)</text>

        {/* Td annotation */}
        <line x1={mx(0)} y1={265} x2={mx(Td)} y2={265} stroke="#c0392b" strokeWidth="1.5"/>
        <line x1={mx(0)} y1={259} x2={mx(0)} y2={271} stroke="#c0392b" strokeWidth="1.5"/>
        <line x1={mx(Td)} y1={259} x2={mx(Td)} y2={271} stroke="#c0392b" strokeWidth="1.5"/>
        <text x={(mx(0)+mx(Td))/2} y={258} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Tᵈ = 2π/ωd</text>

        {/* Axes */}
        <line x1={53} y1={40} x2={53} y2={300} stroke="#555" strokeWidth="1.5" markerEnd="url(#axD)"/>
        <line x1={50} y1={295} x2={552} y2={295} stroke="#555" strokeWidth="1.5" markerEnd="url(#axD)"/>
        <text x={18} y={170} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,18,170)">x(t)</text>
        <text x={555} y={300} fontSize="12" fill="#555">t</text>

        {/* ── RIGHT: parameter panel ─────────── */}
        <rect x={583} y={10} width={307} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={736} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Measurement Method</text>

        {[
          {y:36, bg:"#eaf3ff", bc:"#2E75B6",   label:"Log. Decrement δ",        eq:"δ = ln(x₁/x₂) = 2πζ/√(1−ζ²)", sub:"Ratio of successive peaks"},
          {y:104, bg:"#FEF0F0", bc:"#c0392b",  label:"Damping ratio from δ",    eq:"ζ = δ / √(4π² + δ²)", sub:"Invert to find ζ from test"},
          {y:172, bg:"#f0fff4", bc:"#27ae60",  label:"Damped frequency ωd",      eq:"ωd = ωₙ√(1−ζ²)  [rad/s]", sub:"Actual oscillation frequency"},
          {y:240, bg:"#fff5e6", bc:"#e07b00",  label:"Damped period Td",         eq:"Td = 2π/ωd  [s]", sub:"Measure directly from plot"},
        ].map(({y,bg,bc,label,eq,sub})=>(
          <g key={y}>
            <rect x={589} y={y} width={295} height={62} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={595} y={y+17} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            <text x={595} y={y+36} fontSize="11" fill="#1a1a2e" fontFamily="'Courier New',monospace">{eq}</text>
            <text x={595} y={y+52} fontSize="10" fill="#666" fontStyle="italic">{sub}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Damped free vibration decays exponentially inside the envelope ±Ae^(−ζωₙt). The logarithmic decrement δ = ln(x₁/x₂) is the simplest experimental method to measure damping: record two successive positive peaks, take the natural log of their ratio. Even one peak pair gives a useful estimate of ζ.
      </div>
    </div>
  );
}

// ── Diagram 5: Frequency Response — Amplitude & Phase ────────────
function DiagramFRF() {
  const W=900, H=380;

  const ωn=1, configs2=[
    {ζ:0.05, color:'#9b59b6'},
    {ζ:0.15, color:'#c0392b'},
    {ζ:0.3,  color:'#e07b00'},
    {ζ:0.7,  color:'#27ae60'},
    {ζ:1.0,  color:'#2E75B6'},
  ];

  const nPts=300;
  // Amplitude D(r) = 1/sqrt((1-r²)²+(2ζr)²), capped at 8
  // Phase φ(r) = atan2(2ζr, 1-r²)
  const rMax=2.5;

  const mx = r => 55+(r/rMax)*440;
  const myA = D => 60+(1-Math.min(D,8)/8)*200;  // amplitude: y=60 (max) to 260
  const myP = phi => 280+(phi/Math.PI)*70;        // phase: y=280 (0°) to 350 (180°)

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Frequency Response Function — Amplitude Ratio &amp; Phase Angle</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axF" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
        </defs>

        {/* ── AMPLITUDE plot ────────────────────── */}
        <rect x={10} y={10} width={505} height={260} rx={4} fill="#fafafa" stroke="#ddd"/>
        <text x={263} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Amplitude Ratio  D(r) = X/(F₀/k)</text>

        {/* Amplitude grid */}
        {[0,2,4,6,8].map(v=>(
          <g key={v}>
            <line x1={55} y1={myA(v)} x2={498} y2={myA(v)} stroke="#ebebeb" strokeWidth="0.8"/>
            <text x={49} y={myA(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,0.5,1.0,1.5,2.0,2.5].map(r=>(
          <g key={r}>
            <line x1={mx(r)} y1={58} x2={mx(r)} y2={262} stroke="#ebebeb" strokeWidth="0.8"/>
          </g>
        ))}

        {/* r=1 resonance line */}
        <line x1={mx(1)} y1={58} x2={mx(1)} y2={262} stroke="#c0392b" strokeWidth="1" strokeDasharray="6,3" opacity="0.5"/>
        <text x={mx(1)+3} y={70} fontSize="10" fill="#c0392b">r=1</text>

        {/* D=1 line */}
        <line x1={55} y1={myA(1)} x2={498} y2={myA(1)} stroke="#999" strokeWidth="1" strokeDasharray="5,3"/>
        <text x={500} y={myA(1)+4} fontSize="10" fill="#999">1</text>

        {/* Amplitude curves */}
        {configs2.map(({ζ,color})=>{
          const pts=[];
          for(let i=0;i<=nPts;i++){
            const r=(i/nPts)*rMax;
            const D=1/Math.sqrt((1-r*r)*(1-r*r)+(2*ζ*r)*(2*ζ*r));
            pts.push([r,D]);
          }
          const d=pts.map(([r,D],i)=>`${i===0?'M':'L'}${mx(r).toFixed(1)},${myA(D).toFixed(1)}`).join(' ');
          return <path key={ζ} d={d} fill="none" stroke={color} strokeWidth="2"/>;
        })}

        {/* Axes */}
        <line x1={53} y1={58} x2={53} y2={264} stroke="#555" strokeWidth="1.5"/>
        <line x1={50} y1={262} x2={502} y2={262} stroke="#555" strokeWidth="1.5"/>
        <text x={18} y={165} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,18,165)">D(r)</text>

        {/* ── PHASE plot ───────────────────────── */}
        <rect x={10} y={278} width={505} height={95} rx={4} fill="#fafafa" stroke="#ddd"/>
        <text x={263} y={294} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Phase Angle φ(r)</text>

        {/* Phase grid */}
        {[0,90,180].map(deg=>{
          const phi=deg*Math.PI/180;
          return (
            <g key={deg}>
              <line x1={55} y1={myP(phi)} x2={498} y2={myP(phi)} stroke="#ebebeb" strokeWidth="0.8" strokeDasharray={deg===90?"5,3":""}/>
              <text x={49} y={myP(phi)+4} textAnchor="end" fontSize="10" fill="#888">{deg}°</text>
            </g>
          );
        })}
        {[0,0.5,1.0,1.5,2.0,2.5].map(r=>(
          <g key={r}>
            <text x={mx(r)} y={382} textAnchor="middle" fontSize="10" fill="#888">{r}</text>
          </g>
        ))}
        <text x={263} y={376} textAnchor="middle" fontSize="11" fill="#555">frequency ratio r = ω/ωₙ</text>

        {/* r=1 in phase plot */}
        <line x1={mx(1)} y1={282} x2={mx(1)} y2={370} stroke="#c0392b" strokeWidth="1" strokeDasharray="6,3" opacity="0.5"/>

        {/* Phase curves */}
        {configs2.map(({ζ,color})=>{
          const pts=[];
          for(let i=0;i<=nPts;i++){
            const r=(i/nPts)*rMax;
            const phi=Math.atan2(2*ζ*r,1-r*r);
            const phiAdj = phi<0 ? phi+Math.PI : phi;
            pts.push([r,phiAdj]);
          }
          const d=pts.map(([r,phi],i)=>`${i===0?'M':'L'}${mx(r).toFixed(1)},${myP(phi).toFixed(1)}`).join(' ');
          return <path key={ζ} d={d} fill="none" stroke={color} strokeWidth="2"/>;
        })}

        {/* Phase axis */}
        <line x1={53} y1={282} x2={53} y2={372} stroke="#555" strokeWidth="1.5"/>
        <line x1={50} y1={370} x2={502} y2={370} stroke="#555" strokeWidth="1.5"/>

        {/* ── RIGHT: legend & explanation ─────── */}
        <rect x={522} y={10} width={368} height={H-15} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={706} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">FRF Interpretation</text>

        {/* Legend */}
        {configs2.map(({ζ,color},i)=>(
          <g key={i}>
            <line x1={530} y1={44+i*20} x2={554} y2={44+i*20} stroke={color} strokeWidth="3"/>
            <text x={560} y={48+i*20} fontSize="11" fill={color} fontWeight="700">ζ = {ζ}</text>
          </g>
        ))}

        {[
          {y:152, bg:"#FEF0F0", bc:"#c0392b",  label:"Resonance (r=1, ζ≪1)", lines:["D → 1/(2ζ) at resonance","Quality factor Q = 1/(2ζ)","Peak amplitude: X = F₀/(2kζ)"]},
          {y:232, bg:"#eaf3ff", bc:"#2E75B6",  label:"Quasi-static (r≪1)",   lines:["D ≈ 1   (spring-controlled)","Displacement set by stiffness","Phase ≈ 0°"]},
          {y:312, bg:"#f0fff4", bc:"#27ae60",  label:"Inertia-controlled (r≫1)", lines:["D → 0 (mass attenuates)","Phase → 180°","Vibration isolation region"]},
        ].map(({y,bg,bc,label,lines})=>(
          <g key={y}>
            <rect x={528} y={y} width={356} height={72} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={534} y={y+16} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            {lines.map((l,i)=><text key={i} x={534} y={y+32+i*14} fontSize="11" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The Frequency Response Function (FRF) shows how the steady-state amplitude ratio D(r) and phase lag φ vary with forcing frequency ratio r = ω/ωₙ. At r = 1 (resonance), the amplitude peaks dramatically — the lower the damping, the sharper and taller the peak. Phase always equals 90° at resonance regardless of ζ, making it the most reliable way to identify the natural frequency experimentally.
      </div>
    </div>
  );
}

// ── Diagram 6: Vibration Isolation ───────────────────────────────
function DiagramIsolation() {
  const W=900, H=340;

  const ζvals = [0.05, 0.2, 0.5, 1.0];
  const colors = ['#9b59b6','#c0392b','#e07b00','#2E75B6'];
  const rMax=4, nPts=400;

  // Transmissibility T = sqrt[(1+(2ζr)²)/((1-r²)²+(2ζr)²)]
  const T = (r,ζ) => Math.sqrt((1+(2*ζ*r)*(2*ζ*r))/((1-r*r)*(1-r*r)+(2*ζ*r)*(2*ζ*r)));

  const mx = r => 55+(r/rMax)*500;
  const myT = v => 40+(1-Math.min(v,5)/5)*235;  // log-ish, capped at 5

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Vibration Isolation — Transmissibility TR vs Frequency Ratio</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axI" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
        </defs>

        {/* Graph */}
        <rect x={50} y={35} width={510} height={245} rx={3} fill="#fafafa" stroke="#ddd"/>

        {/* Grid */}
        {[0,1,2,3,4,5].map(v=>(
          <g key={v}>
            <line x1={50} y1={myT(v)} x2={562} y2={myT(v)} stroke={v===1?"#bbb":"#ebebeb"} strokeWidth={v===1?1.5:0.8} strokeDasharray={v===1?"5,3":""}/>
            <text x={44} y={myT(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,1,1.414,2,3,4].map(r=>(
          <g key={r}>
            <line x1={mx(r)} y1={35} x2={mx(r)} y2={282} stroke={r===1.414?"#c0392b":"#ebebeb"} strokeWidth={r===1.414?1.5:0.8} strokeDasharray={r===1.414?"5,3":""}/>
            <text x={mx(r)} y={296} textAnchor="middle" fontSize="10" fill={r===1.414?"#c0392b":"#888"}>{r===1.414?"√2":r}</text>
          </g>
        ))}

        {/* Transmissibility curves */}
        {ζvals.map((ζ,ci)=>{
          const pts=[];
          for(let i=1;i<=nPts;i++){
            const r=(i/nPts)*rMax;
            if(Math.abs(1-r*r)<0.001) {pts.push([r,5]);continue;}
            pts.push([r,T(r,ζ)]);
          }
          const d=pts.map(([r,v],i)=>`${i===0?'M':'L'}${mx(r).toFixed(1)},${myT(Math.min(v,5)).toFixed(1)}`).join(' ');
          return <path key={ζ} d={d} fill="none" stroke={colors[ci]} strokeWidth="2.2"/>;
        })}

        {/* T=1 line */}
        <line x1={50} y1={myT(1)} x2={562} y2={myT(1)} stroke="#999" strokeWidth="1.5" strokeDasharray="8,3"/>
        <text x={565} y={myT(1)+4} fontSize="11" fill="#999">TR=1</text>

        {/* Isolation region shading */}
        <rect x={mx(1.414)} y={35} width={562-mx(1.414)} height={245} fill="#f0fff4" opacity="0.5"/>
        <text x={(mx(1.414)+562)/2} y={60} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">ISOLATION</text>
        <text x={(mx(1.414)+562)/2} y={74} textAnchor="middle" fontSize="11" fill="#27ae60">TR &lt; 1   (r &gt; √2)</text>

        {/* Amplification region shading */}
        <rect x={50} y={35} width={mx(1.414)-50} height={245} fill="#fff5f5" opacity="0.5"/>
        <text x={(50+mx(1.414))/2} y={60} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">AMPLIFICATION</text>
        <text x={(50+mx(1.414))/2} y={74} textAnchor="middle" fontSize="11" fill="#c0392b">TR &gt; 1   (r &lt; √2)</text>

        {/* Axes */}
        <line x1={48} y1={35} x2={48} y2={285} stroke="#555" strokeWidth="1.5" markerEnd="url(#axI)"/>
        <line x1={46} y1={282} x2={566} y2={282} stroke="#555" strokeWidth="1.5" markerEnd="url(#axI)"/>
        <text x={18} y={165} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,18,165)">Transmissibility TR</text>
        <text x={400} y={316} textAnchor="middle" fontSize="12" fill="#555">frequency ratio r = ω/ωₙ</text>

        {/* ── RIGHT panel ─────────────────────── */}
        <rect x={574} y={10} width={316} height={H-15} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={732} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Isolation Design Rules</text>

        {/* Legend */}
        {ζvals.map((ζ,i)=>(
          <g key={i}>
            <line x1={582} y1={44+i*18} x2={606} y2={44+i*18} stroke={colors[i]} strokeWidth="3"/>
            <text x={612} y={48+i*18} fontSize="11" fill={colors[i]} fontWeight="700">ζ = {ζ}</text>
          </g>
        ))}

        {[
          {y:120, bg:"#f0fff4", bc:"#27ae60", label:"Isolation condition",        lines:["r > √2 ≈ 1.414","Mount natural frequency < ω/1.414","Use soft springs for isolation"]},
          {y:200, bg:"#FEF0F0", bc:"#c0392b", label:"Transmissibility formula",   lines:["TR = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)]","TR < 1 → isolating","TR > 1 → amplifying"]},
          {y:280, bg:"#eaf3ff", bc:"#2E75B6", label:"High-r (good isolation)",    lines:["TR ≈ 2ζ/r² for r ≫ √2","More damping can hurt isolation!","At r≫1: lighter damping is better"]},
        ].map(({y,bg,bc,label,lines})=>(
          <g key={y}>
            <rect x={580} y={y} width={304} height={74} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={586} y={y+16} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            {lines.map((l,i)=><text key={i} x={586} y={y+33+i*14} fontSize="11" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Transmissibility TR is the ratio of force transmitted to the base versus the applied force. For r &lt; √2 the mount amplifies vibration (dangerous near resonance). For r &gt; √2 the mount isolates — the green region. Critically: at high r, adding more damping actually reduces isolation effectiveness, so isolation mounts are intentionally lightly damped.
      </div>
    </div>
  );
}

// ── Diagram 7: Phase Portrait & Energy ───────────────────────────
function DiagramPhasePortrait() {
  const W=900, H=360;

  // Phase portrait: underdamped spiral, and energy ellipse
  // ζ=0.08, ωn=2π, x0=1, ẋ0=0
  const ωn2=2*Math.PI, ζ2=0.08, ωd2=ωn2*Math.sqrt(1-ζ2*ζ2);
  const dt=0.005, tMax=4;
  const spiralPts=[];
  for(let t=0;t<=tMax;t+=dt){
    const x=Math.exp(-ζ2*ωn2*t)*Math.cos(ωd2*t);
    const xd=-ζ2*ωn2*Math.exp(-ζ2*ωn2*t)*Math.cos(ωd2*t)-ωd2*Math.exp(-ζ2*ωn2*t)*Math.sin(ωd2*t);
    spiralPts.push([x,xd]);
  }

  // Scale: x:[-1.1,1.1]→[60,280], xd:[-8,8]→[280,60]  (scaled by 1/ωn to normalise)
  const cx=170, cy=180, scX=100, scY=14;
  const px=(x)=>cx+x*scX;
  const py=(xd)=>cy-xd*scY;

  // Undamped ellipse (ζ=0 contour — constant energy)
  const ellipsePts=[];
  for(let th=0;th<=2*Math.PI+0.01;th+=0.02){
    ellipsePts.push([Math.cos(th), ωn2*Math.sin(th)/ωn2*6]);
  }
  // Actually draw it as ellipse element

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Phase Portrait &amp; Energy in SDOF Systems</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axPh" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
          <marker id="arrSp" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
        </defs>

        {/* ── LEFT: Phase portrait ──────────────────── */}
        <rect x={10} y={10} width={390} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={205} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Phase Portrait (x vs ẋ)</text>

        {/* Grid lines */}
        {[-1,-0.5,0,0.5,1].map(v=>(
          <line key={v} x1={60} y1={py(v*6)} x2={280} y2={py(v*6)} stroke="#f0e8e8" strokeWidth="0.8"/>
        ))}
        {[-1,-0.5,0,0.5,1].map(v=>(
          <line key={v} x1={px(v)} y1={50} x2={px(v)} y2={310} stroke="#f0e8e8" strokeWidth="0.8"/>
        ))}

        {/* Undamped ellipse (energy contour) */}
        <ellipse cx={cx} cy={cy} rx={scX} ry={scX*ωn2/ωn2*6*scY/scX*scX} fill="none" stroke="#27ae60" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.6"/>
        {/* rx = scX*1, ry = ωn * scX / scY ... */}
        {/* Let me just use explicit ellipse: rx=scX=100, ry=ωn2*scY = 2π*14 ≈ 88 */}
        <ellipse cx={cx} cy={cy} rx={100} ry={88} fill="none" stroke="#27ae60" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7"/>
        <text x={cx+105} y={cy-85} fontSize="11" fill="#27ae60" fontStyle="italic">undamped</text>
        <text x={cx+105} y={cy-72} fontSize="11" fill="#27ae60" fontStyle="italic">ellipse</text>

        {/* Damped spiral */}
        <path
          d={spiralPts.map(([x,xd],i)=>`${i===0?'M':'L'}${px(x).toFixed(1)},${py(xd/ωn2*6).toFixed(1)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2"/>

        {/* Start point */}
        <circle cx={px(1)} cy={py(0)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={px(1)+7} y={py(0)-5} fontSize="11" fill="#c0392b" fontWeight="700">IC (x₀,0)</text>

        {/* Equilibrium point */}
        <circle cx={cx} cy={cy} r={6} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <text x={cx+8} y={cy-8} fontSize="11" fill="#2E75B6" fontWeight="700">stable</text>
        <text x={cx+8} y={cy+4} fontSize="11" fill="#2E75B6" fontWeight="700">equilibrium</text>

        {/* Axes */}
        <line x1={60} y1={cy} x2={285} y2={cy} stroke="#555" strokeWidth="1.5" markerEnd="url(#axPh)"/>
        <line x1={cx} y1={50} x2={cx} y2={315} stroke="#555" strokeWidth="1.5" markerEnd="url(#axPh)"/>
        <text x={289} y={cy+4} fontSize="12" fill="#555">x</text>
        <text x={cx+5} y={52} fontSize="12" fill="#555">ẋ</text>

        {/* Spiral direction arrow */}
        <path d={`M ${px(0.6)},${py(-3.2)} Q ${px(0.3)},${py(-3.8)} ${px(-0.1)},${py(-3.5)}`} fill="none" stroke="#c0392b" strokeWidth="2" markerEnd="url(#arrSp)"/>

        {/* ── RIGHT: Energy & parameter panels ── */}
        <rect x={410} y={10} width={480} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={650} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Energy &amp; SDOF Characteristics</text>

        {[
          {y:36,  bg:"#f0fff4", bc:"#27ae60", label:"Total mechanical energy E",
            lines:["E = ½mẋ² + ½kx²  (undamped = const.)", "Kinetic T = ½mẋ²  ←→  Potential V = ½kx²","At max displacement: all potential (ẋ=0)","At equilibrium: all kinetic (x=0)"]},
          {y:130, bg:"#FEF0F0", bc:"#c0392b", label:"Power dissipated by damper",
            lines:["P = cẋ²   [Watts]","Energy lost per cycle: ΔE = πcωAd²","Decay: A(t) = A₀e^(−ζωₙt)","Time to halve amplitude: t½ = ln2/(ζωₙ)"]},
          {y:224, bg:"#eaf3ff", bc:"#2E75B6", label:"Phase portrait topology",
            lines:["Undamped: closed ellipses (conservative)","Underdamped: inward spiral → origin","Overdamped: no spiralling, direct approach","Equilibrium at origin: stable focus (ζ<1)"]},
          {y:318, bg:"#fff5e6", bc:"#e07b00", label:"Settlement time (2% criterion)",
            lines:["ts ≈ 4/(ζωₙ)   [seconds]","Time for response to stay within ±2% of x_ss","Longer for small ζ; shorter for larger ζ"]},
        ].map(({y,bg,bc,label,lines})=>(
          <g key={y}>
            <rect x={416} y={y} width={468} height={86} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={422} y={y+17} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            {lines.map((l,i)=><text key={i} x={422} y={y+33+i*14} fontSize="11" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The phase portrait plots velocity ẋ against displacement x. For an undamped system the trajectory is a perfect ellipse (constant energy). With damping, the trajectory spirals inward toward the stable equilibrium at the origin as energy is dissipated. The rate of spiralling reflects the damping ratio ζ — the faster the collapse, the higher the damping.
      </div>
    </div>
  );
}

// ── Diagram 8: Impulse, Step & Harmonic Response Summary ─────────
function DiagramResponseTypes() {
  const W=900, H=340;

  // Impulse response h(t) = (1/mωd) e^(-ζωnt) sin(ωd t)
  // Step response: computed earlier pattern
  // Harmonic: computed from FRF at r=0.6
  const ωn=3, ζ=0.15, ωd=ωn*Math.sqrt(1-ζ*ζ);
  const dt=0.02, tMax=5;

  const impulse=[], step=[], harmonic=[];
  for(let t=0;t<=tMax;t+=dt){
    const h = (1/(ωd))*Math.exp(-ζ*ωn*t)*Math.sin(ωd*t);
    impulse.push([t, h*4]);

    const r=0.6, D=1/Math.sqrt((1-r*r)*(1-r*r)+(2*ζ*r)*(2*ζ*r));
    const phi=Math.atan2(2*ζ*r,1-r*r);
    const hm = D*Math.cos(r*ωn*t - phi);
    harmonic.push([t, hm]);

    // Step
    const xs = 1-Math.exp(-ζ*ωn*t)*(Math.cos(ωd*t)+(ζ/Math.sqrt(1-ζ*ζ))*Math.sin(ωd*t));
    step.push([t,xs]);
  }

  const panels = [
    {x:10,  w:273, label:"Impulse Response h(t)", pts:impulse, color:"#9b59b6", desc:"F(t) = δ(t)", eq:"h(t) = e^{−ζωₙt}sin(ωd t)/mωd", yrange:1.2},
    {x:293, w:273, label:"Step Response x(t)",    pts:step,    color:"#c0392b", desc:"F(t) = F₀·u(t)", eq:"Overshoot = e^{−πζ/√(1−ζ²)}", yrange:1.6},
    {x:576, w:314, label:"Harmonic Response x(t)", pts:harmonic, color:"#2E75B6", desc:"F(t) = F₀cos(ωt), r=0.6", eq:"x(t) = D·cos(ωt−φ)·(F₀/k)", yrange:1.6},
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Standard Input Types &amp; SDOF Responses  [ζ=0.15, ωₙ=3 rad/s]</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {panels.map(({x,w,label,pts,color,desc,eq,yrange})=>{
          const mx = t=>(x+14)+(t/tMax)*(w-28);
          const mid = (H-20)/2+10;
          const scale = 110/yrange;
          const my = y => mid - y*scale;
          const d = pts.map(([t,y],i)=>`${i===0?'M':'L'}${mx(t).toFixed(1)},${my(y).toFixed(1)}`).join(' ');
          return (
            <g key={x}>
              <rect x={x} y={10} width={w} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
              <text x={x+w/2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">{label}</text>
              <text x={x+w/2} y={44} textAnchor="middle" fontSize="11" fill="#888" fontStyle="italic">{desc}</text>

              {/* zero line */}
              <line x1={x+12} y1={mid} x2={x+w-12} y2={mid} stroke="#ccc" strokeWidth="1" strokeDasharray="4,3"/>
              {/* unity line for step */}
              {label.includes('Step') && <line x1={x+12} y1={my(1)} x2={x+w-12} y2={my(1)} stroke="#999" strokeWidth="1" strokeDasharray="5,3"/>}

              {/* curve */}
              <path d={d} fill="none" stroke={color} strokeWidth="2.5"/>

              {/* axes */}
              <line x1={x+12} y1={mid} x2={x+w-8} y2={mid} stroke="#888" strokeWidth="1"/>
              <line x1={x+14} y1={60} x2={x+14} y2={H-30} stroke="#888" strokeWidth="1"/>

              {/* formula box at bottom */}
              <rect x={x+8} y={H-52} width={w-16} height={38} rx={3} fill="#fafafa" stroke={color} strokeWidth="1"/>
              <text x={x+14} y={H-35} fontSize="10" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">{eq}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The three standard test inputs for SDOF systems. The impulse response h(t) (Green's function) is the most fundamental — any arbitrary input can be computed via convolution. The step response is used for control specifications (overshoot, rise time, settling time). The harmonic response is steady-state only and follows the FRF amplitude and phase at the forcing frequency.
      </div>
    </div>
  );
}

// ── Diagram 9: Base Excitation Model ─────────────────────────────
function DiagramBaseExcitation() {
  const W=900, H=310;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Base Excitation — Earthquake &amp; Road Surface Inputs</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="aBE" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
          <marker id="aBEb" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#2E75B6"/></marker>
          <marker id="aBEg" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#27ae60"/></marker>
        </defs>

        {/* ── LEFT: Physical model ──────────────── */}
        <rect x={10} y={10} width={350} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={185} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Base Excitation Model</text>

        {/* Moving base */}
        <rect x={40} y={235} width={270} height={24} rx={3} fill="#888" stroke="#555" strokeWidth="2"/>
        {/* Base motion arrow */}
        <line x1={175} y1={260} x2={175} y2={285} stroke="#e07b00" strokeWidth="3" markerEnd="url(#aBE)"/>
        <text x={185} y={293} fontSize="13" fill="#e07b00" fontWeight="700">y(t) = Y sin(ωt)</text>

        {/* Ground (below moving base) */}
        <line x1={30} y1={295} x2={330} y2={295} stroke="#555" strokeWidth="2"/>
        {[30,52,74,96,118,140,162,184,206,228,250,272,294,316].map(x=>(
          <line key={x} x1={x} y1={295} x2={x-12} y2={310} stroke="#555" strokeWidth="1.5"/>
        ))}

        {/* Spring from base to mass */}
        <path d={springPath(50,195,165,8)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
        <text x={122} y={148} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">k</text>

        {/* Damper from base to mass */}
        <Damper x0={50} x1={195} y={213}/>
        <text x={122} y={200} textAnchor="middle" fontSize="13" fill="#888" fontWeight="700">c</text>

        {/* Vertical connection lines */}
        <line x1={50}  y1={165} x2={50}  y2={235} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4"/>
        <line x1={195} y1={165} x2={195} y2={235} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4"/>

        {/* Mass */}
        <rect x={80} y={80} width={185} height={80} rx={6} fill="#2E75B6" stroke="#1a5276" strokeWidth="2.5"/>
        <text x={173} y={128} textAnchor="middle" fontSize="24" fill="#fff" fontWeight="700">m</text>

        {/* x displacement (absolute) */}
        <line x1={275} y1={82} x2={275} y2={168} stroke="#2E75B6" strokeWidth="2" strokeDasharray="5"/>
        <line x1={265} y1={82} x2={285} y2={82} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={265} y1={168} x2={285} y2={168} stroke="#2E75B6" strokeWidth="2"/>
        <text x={290} y={130} fontSize="12" fill="#2E75B6" fontWeight="700">x(t)</text>

        {/* y displacement (base) */}
        <line x1={315} y1={237} x2={315} y2={168} stroke="#e07b00" strokeWidth="2" strokeDasharray="5"/>
        <text x={320} y={210} fontSize="12" fill="#e07b00" fontWeight="700">y(t)</text>

        {/* Relative z = x−y */}
        <text x={60} y={68} fontSize="12" fill="#c0392b" fontWeight="700">z = x−y  (relative disp.)</text>

        {/* ── RIGHT: Equations & Applications ── */}
        <rect x={368} y={10} width={522} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={629} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Equations &amp; Applications</text>

        {/* EOM box */}
        <rect x={374} y={38} width={510} height={54} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={629} y={58} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Equation of Motion (absolute coordinates)</text>
        <text x={629} y={78} textAnchor="middle" fontSize="12" fill="#1a1a2e" fontFamily="'Courier New',monospace">mẍ + c(ẋ−ẏ) + k(x−y) = 0   →   mẍ + cẋ + kx = cẏ + ky</text>

        {/* Relative coord box */}
        <rect x={374} y={100} width={510} height={44} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={629} y={118} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Relative coordinate z = x − y:</text>
        <text x={629} y={136} textAnchor="middle" fontSize="12" fill="#1a1a2e" fontFamily="'Courier New',monospace">mz̈ + cż + kz = −mÿ = mYω²sin(ωt)</text>

        {/* Transmissibility */}
        <rect x={374} y={152} width={510} height={44} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={629} y={170} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Displacement transmissibility X/Y:</text>
        <text x={629} y={188} textAnchor="middle" fontSize="12" fill="#1a1a2e" fontFamily="'Courier New',monospace">X/Y = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)]  =  same as force TR</text>

        {/* Applications */}
        {[
          {y:204, bg:"#fff5e6", bc:"#e07b00", label:"Vehicle suspension",     text:"Road ↔ y(t); body ↔ x(t). Want X/Y ≪ 1 for passenger comfort. Soft springs help but increase static sag."},
          {y:255, bg:"#f5d0ff", bc:"#9b59b6", label:"Seismic isolation",       text:"Ground ↔ y(t); structure ↔ x(t). Base isolators (r ≫ √2) protect buildings in earthquakes."},
        ].map(({y,bg,bc,label,text})=>(
          <g key={y}>
            <rect x={374} y={y} width={510} height={44} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={380} y={y+16} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}:</text>
            <text x={380} y={y+32} fontSize="11" fill="#444">{text}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        In base excitation, the support point moves with displacement y(t) rather than a force being applied directly to the mass. The transmissibility formula is identical to force transmissibility. Engineering applications include vehicle suspensions (road roughness → passenger isolation) and seismic base isolation (ground motion → building protection).
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────
export default function SDOF() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Single Degree of Freedom (SDOF) Systems</h1>
      <p style={S.p}>
        A single degree of freedom system is one whose configuration is fully described by a single independent coordinate — typically the displacement x(t) of a mass from its equilibrium position. Despite its apparent simplicity, the SDOF system is the cornerstone of all vibration analysis: every complex multi-DOF structure can be decomposed into a set of independent SDOF problems through modal analysis. Mastering SDOF dynamics gives direct physical insight into natural frequencies, damping, resonance, isolation, and response to arbitrary excitation.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Governing EOM: mẍ + cẋ + kx = F(t) — derived by Newton's 2nd Law on the free body diagram",
        "Natural frequency: ωₙ = √(k/m) [rad/s] — frequency of free undamped oscillation",
        "Damping ratio: ζ = c/(2mωₙ) — classifies response: underdamped (ζ<1), critical (ζ=1), overdamped (ζ>1)",
        "Free undamped response: x(t) = A cos(ωₙt) + B sin(ωₙt) — simple harmonic motion at ωₙ",
        "Free damped response: x(t) = e^(−ζωₙt)[A cos(ωd t) + B sin(ωd t)], damped frequency ωd = ωₙ√(1−ζ²)",
        "Steady-state amplitude at forcing frequency ω: X = (F₀/k)·D(r), amplification D = 1/√[(1−r²)²+(2ζr)²]",
        "Resonance at r=ω/ωₙ=1: amplitude peaks at D=1/(2ζ), phase lag = 90° (always, for all ζ)",
        "Vibration isolation requires r > √2 — the mount must be softer than ωₙ < ω/√2",
      ]}/>

      <h2 style={S.h2}>Physical Model &amp; Equation of Motion</h2>
      <p style={S.p}>
        The SDOF model consists of a mass m, a linear spring of stiffness k, and a viscous damper with coefficient c. The mass slides on a frictionless surface and is acted on by an external force F(t). Applying Newton's second law — summing all forces on the isolated mass — gives the equation of motion directly. Each term has a clear physical meaning: the inertia term mẍ resists acceleration, the spring term kx provides a restoring force, and the damping term cẋ dissipates energy.
      </p>
      <DiagramSDOFSetup/>
      <Table2 rows={[
        ["Inertia term  mẍ","Resists acceleration; force proportional to second derivative of displacement\nUnits: [kg·m/s²] = [N]. The larger m, the slower the response."],
        ["Stiffness term  kx","Restoring force proportional to displacement from equilibrium\nUnits: [N/m · m] = [N]. Increases natural frequency."],
        ["Damping term  cẋ","Velocity-proportional resistive force; dissipates energy as heat\nUnits: [N·s/m · m/s] = [N]. Reduces amplitude of oscillation."],
        ["Initial conditions","x(0) = x₀ (initial displacement), ẋ(0) = ẋ₀ (initial velocity)\nCompletely determine the free response when F(t)=0"],
        ["General solution","x(t) = x_homogeneous(t) + x_particular(t)\nTransient (free) + Steady-state (forced) superposition"],
      ]}/>

      <h2 style={S.h2}>Free Undamped Vibration</h2>
      <p style={S.p}>
        With no damping (c = 0) and no external force (F = 0), the equation of motion reduces to mẍ + kx = 0. The solution is pure simple harmonic motion at the natural frequency ωₙ = √(k/m). This is the idealised case — no energy is lost, so the system oscillates forever at constant amplitude. The amplitude and phase of oscillation are set entirely by the initial conditions.
      </p>
      <DiagramFreeUndamped/>
      <Table2 rows={[
        ["SHM solution","x(t) = x₀ cos(ωₙt) + (ẋ₀/ωₙ) sin(ωₙt) = A cos(ωₙt − φ)\nA = √(x₀² + (ẋ₀/ωₙ)²),   φ = arctan(ẋ₀/(ωₙx₀))"],
        ["Natural frequency ωₙ","ωₙ = √(k/m)   [rad/s]\nDoubly stiff (2k) → ωₙ increases by √2; double mass → ωₙ decreases by √2"],
        ["Cyclic frequency fₙ","fₙ = ωₙ/(2π)   [Hz = cycles/s]\nThe frequency you'd hear or measure with an accelerometer"],
        ["Period T","T = 1/fₙ = 2π/ωₙ   [seconds]\nTime for one complete oscillation cycle"],
        ["Static deflection xst","xst = F₀/k under static load F₀\nUseful link: ωₙ = √(g/xst)  — measure deflection to find ωₙ"],
        ["Energy conservation","E = ½mẋ² + ½kx² = constant\nEnergy converts between kinetic (peak at x=0) and potential (peak at x=±A)"],
      ]}/>

      <h2 style={S.h2}>Free Damped Vibration &amp; Logarithmic Decrement</h2>
      <p style={S.p}>
        Adding viscous damping (c ≠ 0) causes the oscillation amplitude to decay exponentially. The damping ratio ζ = c/(2mωₙ) classifies the response: underdamped (ζ &lt; 1) systems oscillate while decaying; critically damped (ζ = 1) systems return to rest fastest without overshoot; overdamped (ζ &gt; 1) systems creep back without oscillating. In practice the vast majority of lightly damped structures are underdamped with ζ between 0.01 and 0.1.
      </p>
      <DiagramLogDecrement/>
      <Table2 rows={[
        ["Underdamped solution","x(t) = e^{−ζωₙt}[A cos(ωd t) + B sin(ωd t)]\nDecaying sinusoid; A,B set by initial conditions"],
        ["Damped frequency ωd","ωd = ωₙ√(1−ζ²)   [rad/s]\nActual frequency of oscillation; always < ωₙ\nFor ζ=0.1: ωd ≈ 0.995ωₙ  (barely changes for light damping)"],
        ["Critical damping cc","cc = 2mωₙ = 2√(km)\nMinimum c to prevent oscillation\nζ = c/cc"],
        ["Logarithmic decrement δ","δ = ln(x₁/x₂) = 2πζ/√(1−ζ²)\nPractical test: drop system, measure two successive positive peaks\nFor small ζ: δ ≈ 2πζ"],
        ["Damping ratio from δ","ζ = δ/√(4π² + δ²)\nFor small δ: ζ ≈ δ/(2π)\nUsed in experimental modal analysis"],
        ["Time to halve amplitude","t½ = ln2/(ζωₙ) ≈ 0.693/(ζωₙ)\nStructural: ζ≈0.02 → very slow decay; rubber: ζ≈0.1 → faster"],
      ]}/>

      <h2 style={S.h2}>Damping Regimes — Step Response Comparison</h2>
      <DiagramDampingComparison/>
      <Table2 rows={[
        ["Underdamped  ζ < 1","Oscillates about final value; amplitude of oscillation decays\nPeak overshoot: OS% = exp(−πζ/√(1−ζ²)) × 100%\nζ=0.2 → OS≈53%; ζ=0.5 → OS≈16%; ζ=0.7 → OS≈5%"],
        ["Critically damped  ζ = 1","Fastest approach to final value without overshoot\nc = cc = 2√(km) exactly\nUsed in precision instruments, door closers"],
        ["Overdamped  ζ > 1","Two distinct real poles; exponential approach, no oscillation\nSlower than critical damping; rarely optimal\nAppears in: heavy fluid dashpots, some seismic isolators"],
        ["Optimal for control","ζ ≈ 0.5–0.7 balances speed and overshoot\nζ=0.707 (1/√2) minimises ITAE (integrated time-absolute error)\nMost PID-tuned control loops target this range"],
        ["Settling time ts","ts ≈ 4/(ζωₙ)  for 2% criterion\nts ≈ 3/(ζωₙ)  for 5% criterion\nProducts of ζωₙ = σ (real part of complex eigenvalue)"],
        ["Rise time tr","tr ≈ (1.8)/ωₙ for ζ=0 (undamped)\ntr increases with ζ\nTrade-off: faster rise → more overshoot"],
      ]}/>

      <h2 style={S.h2}>Steady-State Harmonic Response &amp; Resonance</h2>
      <p style={S.p}>
        When the system is driven by a harmonic force F(t) = F₀ cos(ωt), after the transient decays a steady-state oscillation at the forcing frequency ω persists. The amplitude of this oscillation is amplified or attenuated relative to the static deflection F₀/k by the dynamic amplification factor D(r), which depends on the frequency ratio r = ω/ωₙ and the damping ratio ζ. Near resonance (r ≈ 1), even very small forces can produce dangerously large responses in lightly damped systems.
      </p>
      <DiagramFRF/>
      <Table2 rows={[
        ["Steady-state amplitude","X = (F₀/k) · D(r)\nD(r) = 1/√[(1−r²)² + (2ζr)²]  (dimensionless amplification)"],
        ["Phase lag φ","tan φ = 2ζr/(1−r²)\nφ = 0° for r≪1 (in-phase with force)\nφ = 90° at r=1 (resonance, for ALL ζ)\nφ → 180° for r≫1 (out-of-phase, mass-controlled)"],
        ["Quality factor Q","Q = 1/(2ζ) = peak D at resonance\nAlso: Q = ωₙ/Δω  where Δω is −3dB bandwidth\nHigh Q = narrow, tall resonance peak"],
        ["Half-power bandwidth","Δω = ωₙ/Q = 2ζωₙ\nFrequency range where D ≥ D_max/√2\nUsed experimentally to measure ζ from FRF plots"],
        ["Anti-resonance","Dynamic absorber attached to SDOF creates anti-resonance\nAmplitude drops to zero at tuned absorber frequency\nUsed in tall buildings (tuned mass dampers)"],
        ["Resonance identification","Phase = 90° at natural frequency regardless of ζ\nMore reliable than peak amplitude for identifying ωₙ\nStandard method in experimental modal analysis"],
      ]}/>

      <h2 style={S.h2}>Vibration Isolation</h2>
      <p style={S.p}>
        Vibration isolation mounts are designed to prevent transmission of vibratory forces from machinery to its support structure (force isolation) or from a vibrating base to the payload (motion isolation). The key parameter is transmissibility TR — the ratio of transmitted to applied force (or displacement). Isolation only occurs for r &gt; √2, requiring the mount's natural frequency to be well below the disturbance frequency.
      </p>
      <DiagramIsolation/>
      <Table2 rows={[
        ["Transmissibility TR","TR = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)]\nTR < 1: isolating;  TR > 1: amplifying\nAt resonance (r=1): TR = 1+1/(2ζ) ≈ large"],
        ["Isolation condition","r > √2 ≈ 1.414  (mount ωₙ < ω/√2)\nFor 100Hz machine, ωₙ < 71Hz → fₙ < 11Hz\nTypical rubber mounts: fₙ ≈ 3–15 Hz"],
        ["High-r isolation","TR ≈ 2ζ/r²  for r ≫ √2\nMore damping degrades high-frequency isolation!\nCompromise: low damping + snubbers to limit resonance motion"],
        ["Static deflection & fₙ","fₙ = (1/2π)√(g/xst)\nxst = 1mm → fₙ ≈ 16Hz; xst = 25mm → fₙ ≈ 3.2Hz\nSofter mount (lower fₙ) → better isolation at given ω"],
        ["Pneumatic isolators","Variable stiffness; fₙ as low as 1–2 Hz\nUsed for optical tables, MRI machines, precision CMMs"],
        ["Active isolation","Actuators + accelerometers + control; can achieve fₙ → 0\nUsed in semiconductor lithography, gravitational wave detectors"],
      ]}/>

      <h2 style={S.h2}>Phase Portrait &amp; Energy</h2>
      <DiagramPhasePortrait/>
      <Table2 rows={[
        ["Phase plane","Plot of ẋ vs x; each initial condition traces one trajectory\nUndamped: closed ellipses (conservative system)\nDamped: spirals converging to origin"],
        ["Stable equilibrium","Origin (x=0, ẋ=0); all trajectories approach it if ζ>0\nClassification: stable focus (0<ζ<1), stable node (ζ≥1)"],
        ["Total energy E","E = T + V = ½mẋ² + ½kx²\nUndamped: constant along trajectory\nDamped: dE/dt = −cẋ² ≤ 0 (always decreasing)"],
        ["Energy per cycle","ΔE = πcωd A²  (energy dissipated per oscillation cycle)\nLoss factor η = ΔE/(2πE_max) = 2ζ  (for small ζ)"],
        ["Poincaré section","Sample phase portrait once per period; reveals chaos/periodicity\nSDOF forced: single point (periodic), cloud (chaotic)"],
      ]}/>

      <h2 style={S.h2}>Standard Response Types</h2>
      <p style={S.p}>
        SDOF systems are characterised by how they respond to three standard input types: the impulse (instantaneous kick), the step (sudden constant force), and the harmonic (sinusoidal) excitation. The impulse response h(t) is the most fundamental — it is the system's Green's function, and any general input can be reconstructed via the convolution integral (Duhamel's integral).
      </p>
      <DiagramResponseTypes/>
      <Table2 rows={[
        ["Impulse response h(t)","h(t) = [1/(mωd)] e^{−ζωₙt} sin(ωd t)\nDimension: m/(N·s) = 1/(kg·s)\nFundamental: x(t) = ∫₀ᵗ F(τ)h(t−τ)dτ  (Duhamel/convolution)"],
        ["Step response","x_ss = F₀/k  (final value)\nPeak overshoot at t_p = π/ωd\nOS% = 100·exp(−πζ/√(1−ζ²))"],
        ["Harmonic steady-state","x(t) = X cos(ωt − φ)  where X = D·F₀/k, φ = arctan(2ζr/(1−r²))\nTransient decays; only this term remains after many cycles"],
        ["Duhamel's integral","x(t) = (1/mωd)∫₀ᵗ F(τ) e^{−ζωₙ(t−τ)} sin(ωd(t−τ)) dτ\nAllows computation of response to ANY arbitrary input F(t)"],
        ["Laplace transfer function","H(s) = 1/(ms²+cs+k) = (1/k)/[(s/ωₙ)²+2ζ(s/ωₙ)+1]\nPoles: s = −ζωₙ ± jωd  (complex conjugate pair for ζ<1)"],
        ["Step input via Laplace","X(s) = H(s)·(F₀/s)\nPartial fractions → time domain step response\nVerifies initial and final value theorems"],
      ]}/>

      <h2 style={S.h2}>Base Excitation</h2>
      <p style={S.p}>
        In many practical situations the excitation enters through the support point rather than as a force applied directly to the mass. Earthquakes shake the ground; road roughness moves the wheel axle; a ship's deck moves with wave motion. The governing equation is reformulated in terms of relative displacement z = x − y, which converts the problem into one with an equivalent applied force equal to −mÿ(t).
      </p>
      <DiagramBaseExcitation/>
      <Table2 rows={[
        ["Absolute EOM","mẍ + c(ẋ−ẏ) + k(x−y) = 0\nRearranged: mẍ + cẋ + kx = cẏ + ky"],
        ["Relative coord EOM","z = x−y; mz̈ + cż + kz = −mÿ\nEffective force = −mÿ (base acceleration × mass)\nSame form as direct force excitation"],
        ["Displacement TR  X/Y","X/Y = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)] = same as force TR\nFor isolation: want X/Y ≪ 1 → need r ≫ √2"],
        ["Acceleration TR  Ẍ/Ÿ","Same formula as X/Y (for harmonic base)\nAccelerometers measure this — used in shock/vibration testing"],
        ["Vehicle suspension","m=car body, k/c=suspension, y=road\nRide quality ↑ with softer springs (lower ωₙ)\nRoll stiffness ↑ with stiffer springs (handling) — trade-off"],
        ["Seismic isolation","Base isolators (lead-rubber bearings, friction pendulums)\nfₙ ≈ 0.5–1 Hz  →  r ≫ 1 for earthquake content (1–10 Hz)\nReduces floor accelerations by factor ~5–10"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Equation of Motion:  mẍ + cẋ + kx = F(t)",
        "Natural frequency:  ωₙ = √(k/m)  [rad/s]   |   fₙ = ωₙ/(2π)  [Hz]   |   T = 2π/ωₙ  [s]",
        "Damping ratio:  ζ = c/(2mωₙ) = c/(2√(km))   |   Critical damping: cc = 2√(km)",
        "Damped frequency:  ωd = ωₙ√(1−ζ²)   [rad/s]",
        "Free damped response:  x(t) = e^(−ζωₙt)[A cos(ωd t) + B sin(ωd t)]",
        "Log decrement:  δ = ln(x₁/x₂) = 2πζ/√(1−ζ²)   →   ζ = δ/√(4π²+δ²)",
        "Dynamic amplification:  D(r) = 1/√[(1−r²)²+(2ζr)²]   where r = ω/ωₙ",
        "Phase lag:  φ = arctan[2ζr/(1−r²)]   (= 90° at resonance for all ζ)",
        "Quality factor:  Q = 1/(2ζ)   |   Bandwidth:  Δω = 2ζωₙ",
        "Transmissibility:  TR = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)]",
        "Settling time:  ts ≈ 4/(ζωₙ)  [2% criterion]   |   Overshoot: OS% = 100·e^(−πζ/√(1−ζ²))",
        "Impulse response:  h(t) = e^(−ζωₙt)sin(ωd t)/(mωd)   |   Duhamel: x(t) = ∫F(τ)h(t−τ)dτ",
      ]}/>
    </div>
  );
}